import { Output, ViewChild, EventEmitter, Component, OnInit, Input, OnDestroy } from '@angular/core';
import { ValidationErrors, UntypedFormGroup, UntypedFormBuilder, Validators, AbstractControl } from '@angular/forms';
import { Observable, empty } from 'rxjs';
import { takeWhile, map, catchError } from 'rxjs';
import { ExpiryType, PmaDocumentCentreCategory } from '@feature/pma-document-centre/pma-document-centre-types';
import * as lodash from 'lodash';
import { C } from '@angular/cdk/keycodes';

const clone = lodash.cloneDeep;


@Component({
  selector: 'mojo-pma-document-centre-category-dialog',
  templateUrl: './pma-document-centre-category-dialog.component.html',
})
export class PmaDocumentCentreCategoryDialogComponent implements OnInit {

  @Input() expiryTypes: ExpiryType[] = [];
  dialogTitle: string;
  form: UntypedFormGroup;
  displayDialog: boolean;

  @Output() onSave = new EventEmitter<PmaDocumentCentreCategory>();

  private category: PmaDocumentCentreCategory;

  @Input() existingCategories: PmaDocumentCentreCategory[];

  constructor(private readonly formBuilder: UntypedFormBuilder) { }

  ngOnInit() {
    this.buildForm();
  }

  save() {
    const category = this.mapFormToCategory();
    this.onSave.emit(category);
    this.reset();
    this.displayDialog = false;
  }

  private reset() {
    this.form.reset();
    this.category = null;
  }

  cancel() {
    this.reset();
    this.displayDialog = false;
  }

  showAddDialog() {
    this.dialogTitle = 'Add Category';
    this.displayDialog = true;
    this.buildForm();
  }

  showEditDialog(category: PmaDocumentCentreCategory) {
    this.category = clone(category);
    this.dialogTitle = 'Edit Category';
    this.displayDialog = true;
    this.buildForm();
    this.mapToForm(category);
  }


  private hasExpiryType(): boolean {
    return this.selectedExpiryType.value && this.duration.value;
  }

  private mapFormToCategory(): PmaDocumentCentreCategory {

    return {
      key: this.category ? this.category.key : null,
      name: this.name.value,
      durationMins: this.hasExpiryType() ? this.selectedExpiryType.value.toMins(this.duration.value) : null,
      expiryType: this.hasExpiryType() ? this.selectedExpiryType.value as ExpiryType : null
    }

  }

  private mapToForm(category: PmaDocumentCentreCategory) {
    this.name.setValue(category.name);
    if (category.expiryType) {
      this.duration.setValue(category.expiryType.fromMins(category.durationMins));
      this.selectedExpiryType.setValue(category.expiryType);
    } else {
      this.selectedExpiryType.setValue(this.expiryTypes[0]);
    }
  }

  private getControl(name: string): AbstractControl {
    return this.form ? this.form.controls[name] : null;
  }

  get name(): AbstractControl {
    return this.getControl('name');
  }

  get duration(): AbstractControl {
    return this.getControl('duration');
  }

  get selectedExpiryType(): AbstractControl {
    return this.getControl('selectedExpiryType');
  }

  private isRenamingCategory(value: string) {
    return this.category && this.category.name !== value;
  }

  private isCreatingCategory() { return !this.category; }

  private doesCategoryAlreadyExist(value: string) {
    const normalisedValue = value?.trim().toLowerCase();
    return this.existingCategories
        && this.existingCategories.find(cat => cat.name?.trim().toLowerCase() == normalisedValue);
  }

  private getCategoryNameValidatorValidatorFn() {
    return (control: AbstractControl): ValidationErrors | null => {
      const hasDuplicate = (this.isRenamingCategory(control.value) || this.isCreatingCategory())
                        && this.doesCategoryAlreadyExist(control.value);
      return hasDuplicate ? { 'name_already_exists': true } : null;
    }
  }


  private buildForm(): void {
    const nameMaxlength = 50;

    this.form = this.formBuilder.group(
      {
        name: ['', [
          Validators.required,
          Validators.maxLength(nameMaxlength),
          this.getCategoryNameValidatorValidatorFn(),
        ]],
        duration: ['', Validators.max(999)],
        selectedExpiryType: [this.expiryTypes[0]]
      }
    );
  }

}
